import React, { useEffect, useState } from 'react';
import { Trophy, Medal, TrendingUp, Volume2, Maximize2, VolumeX, Minimize2, Zap, Play } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { sellers as initialSellers } from '../utils/mockData';
import { formatCurrency } from '../utils/format';
import { EvolutionChart } from '../components/ranking/EvolutionChart';
import { SaleNotificationPopup } from '../components/ranking/SaleNotificationPopup';
import { RankingChangePopup } from '../components/ranking/RankingChangePopup';
// Helper to generate random integer
const randomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1) + min);
const PRODUCT_NAMES = ['Kit Premium Maquiagem', 'Perfume Importado Floral', 'Conjunto Skin Care', 'Paleta de Sombras Pro', 'Kit Batons Matte', 'Hidratante Facial Gold', 'Máscara Capilar Reconstrutora'];
export function Ranking() {
  const {
    addToast
  } = useToast();
  const [period, setPeriod] = useState('Mensal');
  const [soundEnabled, setSoundEnabled] = useState(true); // Default to true for demo
  const [isTVMode, setIsTVMode] = useState(false);
  // State for sellers to allow updates
  const [sellers, setSellers] = useState(initialSellers);
  // Popup States
  const [salePopup, setSalePopup] = useState<{
    isOpen: boolean;
    sellerName: string;
    sellerAvatar: string;
    productName: string;
    value: number;
  }>({
    isOpen: false,
    sellerName: '',
    sellerAvatar: '',
    productName: '',
    value: 0
  });
  const [rankingPopup, setRankingPopup] = useState<{
    isOpen: boolean;
    sellerName: string;
    sellerAvatar: string;
    newRank: number;
    overtakenName?: string;
  }>({
    isOpen: false,
    sellerName: '',
    sellerAvatar: '',
    newRank: 0
  });
  // Derived state for sorted sellers
  const sortedSellers = [...sellers].sort((a, b) => b.salesCount - a.salesCount);
  const top3 = sortedSellers.slice(0, 3);
  const restOfSellers = sortedSellers.slice(3);
  // Split remaining sellers into two columns
  const midPoint = Math.ceil(restOfSellers.length / 2);
  const leftColumnSellers = restOfSellers.slice(0, midPoint);
  const rightColumnSellers = restOfSellers.slice(midPoint);
  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
    addToast(soundEnabled ? 'Sons de notificação desativados' : 'Sons de notificação ativados', 'info');
  };
  const toggleTVMode = () => {
    if (!isTVMode) {
      document.documentElement.requestFullscreen().catch(e => {
        console.error(e);
      });
      addToast('Modo TV ativado - Pressione ESC para sair', 'success');
    } else {
      document.exitFullscreen().catch(e => {
        console.error(e);
      });
    }
    setIsTVMode(!isTVMode);
  };
  // Listen for fullscreen change (ESC key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsTVMode(false);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);
  // --- DEMO SIMULATION LOGIC ---
  const simulateSale = () => {
    // Pick a random seller
    const randomIndex = randomInt(0, sellers.length - 1);
    const seller = sellers[randomIndex];
    const saleValue = randomInt(500, 5000);
    const product = PRODUCT_NAMES[randomInt(0, PRODUCT_NAMES.length - 1)];
    // Update seller stats
    const updatedSellers = [...sellers];
    updatedSellers[randomIndex] = {
      ...seller,
      salesCount: seller.salesCount + 1,
      revenue: seller.revenue + saleValue,
      rankingChange: 1 // Indicate positive movement
    };
    setSellers(updatedSellers);
    // Show popup
    setSalePopup({
      isOpen: true,
      sellerName: seller.name,
      sellerAvatar: seller.avatar,
      productName: product,
      value: saleValue
    });
  };
  const simulateRankingChange = () => {
    // Force a change in the top 3
    // Let's take the 2nd place and make them 1st by adding enough sales
    // Or take 4th and make them 3rd
    // Strategy: Find 4th place and boost them past 3rd
    const currentSorted = [...sellers].sort((a, b) => b.salesCount - a.salesCount);
    if (currentSorted.length < 4) return; // Safety check
    const thirdPlace = currentSorted[2];
    const fourthPlace = currentSorted[3];
    // Calculate needed sales to overtake
    const salesDiff = thirdPlace.salesCount - fourthPlace.salesCount + 1;
    const revenueDiff = thirdPlace.revenue - fourthPlace.revenue + 1000;
    const updatedSellers = sellers.map(s => {
      if (s.id === fourthPlace.id) {
        return {
          ...s,
          salesCount: s.salesCount + salesDiff,
          revenue: s.revenue + revenueDiff,
          rankingChange: 3 // Big jump
        };
      }
      if (s.id === thirdPlace.id) {
        return {
          ...s,
          rankingChange: -1 // Dropped
        };
      }
      return s;
    });
    setSellers(updatedSellers);
    // Show popup
    setRankingPopup({
      isOpen: true,
      sellerName: fourthPlace.name,
      sellerAvatar: fourthPlace.avatar,
      newRank: 3,
      overtakenName: thirdPlace.name
    });
  };
  const Content = () => <div className={`space-y-6 relative ${isTVMode ? 'p-8 bg-background min-h-screen' : ''}`} data-id="element-580">
      {/* Popups */}
      <SaleNotificationPopup {...salePopup} onClose={() => setSalePopup(prev => ({
      ...prev,
      isOpen: false
    }))} data-id="element-581" />
      <RankingChangePopup {...rankingPopup} onClose={() => setRankingPopup(prev => ({
      ...prev,
      isOpen: false
    }))} data-id="element-582" />

      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-583">
        <div className="flex bg-slate-800 p-1 rounded-lg" data-id="element-584">
          {['Mensal', 'Trimestral', 'Anual'].map(p => <button key={p} onClick={() => setPeriod(p)} className={`
                px-4 py-1.5 rounded-md text-sm font-medium transition-all
                ${period === p ? 'bg-primary text-white shadow-lg' : 'text-text-muted hover:text-white'}
              `} data-id="element-585">
              {p}
            </button>)}
        </div>

        <div className="flex gap-2" data-id="element-586">
          <Button variant={soundEnabled ? 'primary' : 'ghost'} size="sm" onClick={toggleSound} icon={soundEnabled ? <Volume2 className="h-4 w-4" data-id="element-588" /> : <VolumeX className="h-4 w-4" data-id="element-589" />} data-id="element-587">
            Sons
          </Button>
          <Button variant={isTVMode ? 'primary' : 'ghost'} size="sm" onClick={toggleTVMode} icon={isTVMode ? <Minimize2 className="h-4 w-4" data-id="element-591" /> : <Maximize2 className="h-4 w-4" data-id="element-592" />} data-id="element-590">
            {isTVMode ? 'Sair TV' : 'Modo TV'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-593">
        {/* Podium Section (Left) - Compact Design */}
        <div className="lg:col-span-1 flex flex-col" data-id="element-594">
          <div className="relative h-[400px] bg-gradient-to-b from-slate-800/50 to-slate-900/50 rounded-2xl border border-white/5 p-4 flex items-end justify-center gap-2 overflow-hidden" data-id="element-595">
            {/* Confetti Effect Background (Simulated) */}
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" data-id="element-596" />

            {/* 2nd Place */}
            {top3[1] && <div className="flex flex-col items-center z-10 animate-in slide-in-from-bottom-10 duration-700 delay-100 mb-4 transition-all" data-id="element-597">
                <Avatar src={top3[1].avatar} fallback={top3[1].name} size="lg" className="mb-2 ring-4 ring-slate-300" data-id="element-598" />
                <div className="flex flex-col items-center bg-slate-800/80 backdrop-blur-sm p-3 rounded-t-xl w-20 h-28 border-t-4 border-slate-300 shadow-lg" data-id="element-599">
                  <Medal className="h-5 w-5 text-slate-300 mb-1" data-id="element-600" />
                  <span className="text-xl font-bold text-white" data-id="element-601">2</span>
                  <span className="text-[10px] text-center font-medium mt-auto truncate w-full" data-id="element-602">
                    {top3[1].name.split(' ')[0]}
                  </span>
                  <span className="text-[9px] text-text-muted" data-id="element-603">
                    {top3[1].salesCount}
                  </span>
                </div>
              </div>}

            {/* 1st Place */}
            {top3[0] && <div className="flex flex-col items-center z-20 animate-in slide-in-from-bottom-10 duration-700 transition-all" data-id="element-604">
                <div className="relative" data-id="element-605">
                  <Trophy className="absolute -top-6 left-1/2 -translate-x-1/2 h-6 w-6 text-yellow-400 animate-bounce" data-id="element-606" />
                  <Avatar src={top3[0].avatar} fallback={top3[0].name} size="xl" className="mb-2 ring-4 ring-yellow-400 shadow-lg shadow-yellow-400/20" data-id="element-607" />
                </div>
                <div className="flex flex-col items-center bg-gradient-to-b from-yellow-500/20 to-slate-800/80 backdrop-blur-sm p-3 rounded-t-xl w-24 h-40 border-t-4 border-yellow-400 shadow-xl" data-id="element-608">
                  <Medal className="h-6 w-6 text-yellow-400 mb-1" data-id="element-609" />
                  <span className="text-3xl font-bold text-white" data-id="element-610">1</span>
                  <span className="text-xs text-center font-bold mt-auto truncate w-full text-yellow-200" data-id="element-611">
                    {top3[0].name.split(' ')[0]}
                  </span>
                  <span className="text-[10px] text-yellow-100/80" data-id="element-612">
                    {top3[0].salesCount} vendas
                  </span>
                </div>
              </div>}

            {/* 3rd Place */}
            {top3[2] && <div className="flex flex-col items-center z-10 animate-in slide-in-from-bottom-10 duration-700 delay-200 mb-2 transition-all" data-id="element-613">
                <Avatar src={top3[2].avatar} fallback={top3[2].name} size="lg" className="mb-2 ring-4 ring-amber-700" data-id="element-614" />
                <div className="flex flex-col items-center bg-slate-800/80 backdrop-blur-sm p-3 rounded-t-xl w-20 h-20 border-t-4 border-amber-700 shadow-lg" data-id="element-615">
                  <Medal className="h-5 w-5 text-amber-700 mb-1" data-id="element-616" />
                  <span className="text-xl font-bold text-white" data-id="element-617">3</span>
                  <span className="text-[10px] text-center font-medium mt-auto truncate w-full" data-id="element-618">
                    {top3[2].name.split(' ')[0]}
                  </span>
                  <span className="text-[9px] text-text-muted" data-id="element-619">
                    {top3[2].salesCount}
                  </span>
                </div>
              </div>}
          </div>
        </div>

        {/* List Section (Right) - 2 Columns */}
        <div className="lg:col-span-2 flex flex-col h-[400px]" data-id="element-620">
          <div className="flex items-center justify-between mb-2" data-id="element-621">
            <h2 className="text-xl font-bold text-white" data-id="element-622">Ranking Geral</h2>
            <div className="flex items-center gap-2 text-xs text-emerald-400 animate-pulse" data-id="element-623">
              <span className="h-2 w-2 rounded-full bg-emerald-500" data-id="element-624" />
              Atualizado em tempo real
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full overflow-hidden" data-id="element-625">
            {/* Left Column (Pos 4-8 approx) */}
            <Card className="divide-y divide-slate-700/50 overflow-y-auto custom-scrollbar" data-id="element-626">
              {leftColumnSellers.map((seller, index) => <RankingRow key={seller.id} seller={seller} rank={index + 4} topSales={top3[0]?.salesCount || 100} data-id="element-627" />)}
            </Card>

            {/* Right Column (Pos 9-12 approx) */}
            <Card className="divide-y divide-slate-700/50 overflow-y-auto custom-scrollbar" data-id="element-628">
              {rightColumnSellers.map((seller, index) => <RankingRow key={seller.id} seller={seller} rank={index + 4 + leftColumnSellers.length} topSales={top3[0]?.salesCount || 100} data-id="element-629" />)}
            </Card>
          </div>
        </div>
      </div>

      {/* Evolution Chart Section */}
      <div className="animate-in fade-in slide-in-from-bottom-4 duration-700" data-id="element-630">
        <EvolutionChart period={period} data-id="element-631" />
      </div>

      {/* Hidden Demo Controls */}
      {!isTVMode && <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-50" data-id="element-632">
          <div className="group relative" data-id="element-633">
            <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none" data-id="element-634">
              Simular Mudança Top 3
            </div>
            <button onClick={simulateRankingChange} className="w-12 h-12 rounded-full bg-yellow-500 hover:bg-yellow-400 text-slate-900 shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95" aria-label="Simular Mudança no Ranking" data-id="element-635">
              <Trophy className="w-6 h-6" data-id="element-636" />
            </button>
          </div>

          <div className="group relative" data-id="element-637">
            <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none" data-id="element-638">
              Simular Venda
            </div>
            <button onClick={simulateSale} className="w-12 h-12 rounded-full bg-primary hover:bg-primary-light text-white shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95 animate-pulse" aria-label="Simular Nova Venda" data-id="element-639">
              <Zap className="w-6 h-6" data-id="element-640" />
            </button>
          </div>
        </div>}
    </div>;
  if (isTVMode) {
    return <Content data-id="element-641" />;
  }
  return <Layout data-id="element-642">
      <Content data-id="element-643" />
    </Layout>;
}
// Helper component for ranking rows to avoid duplication
function RankingRow({
  seller,
  rank,
  topSales
}: {
  seller: any;
  rank: number;
  topSales: number;
}) {
  return <div className="p-3 flex items-center gap-3 hover:bg-slate-800/50 transition-colors" data-id="element-644">
      <span className="w-6 text-center font-bold text-sm text-text-muted" data-id="element-645">
        {rank}º
      </span>

      <Avatar src={seller.avatar} fallback={seller.name} size="sm" data-id="element-646" />

      <div className="flex-1 min-w-0" data-id="element-647">
        <div className="flex justify-between mb-1" data-id="element-648">
          <span className="font-medium text-white text-sm truncate" data-id="element-649">
            {seller.name}
          </span>
          <span className="font-bold text-primary-light text-xs" data-id="element-650">
            {formatCurrency(seller.revenue)}
          </span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-1 overflow-hidden" data-id="element-651">
          <div className="bg-gradient-to-r from-primary to-secondary h-full rounded-full transition-all duration-1000" style={{
          width: `${seller.salesCount / topSales * 100}%`
        }} data-id="element-652" />
        </div>
      </div>

      <div className="text-right min-w-[50px]" data-id="element-653">
        <div className="text-sm font-bold text-white" data-id="element-654">{seller.salesCount}</div>
      </div>

      <div className={`
        flex items-center text-[10px] font-medium w-8 justify-end
        ${(seller.rankingChange || 0) > 0 ? 'text-emerald-400' : (seller.rankingChange || 0) < 0 ? 'text-red-400' : 'text-text-muted'}
      `} data-id="element-655">
        {(seller.rankingChange || 0) !== 0 && <TrendingUp className={`h-2.5 w-2.5 mr-0.5 ${(seller.rankingChange || 0) < 0 ? 'rotate-180' : ''}`} data-id="element-656" />}
        {Math.abs(seller.rankingChange || 0) > 0 ? Math.abs(seller.rankingChange || 0) : '-'}
      </div>
    </div>;
}